import React, { useRef, useState } from 'react';
import { Loader2, X, FileText, Trash2, Upload } from 'lucide-react';

import { Button } from '../../ui/button';
import useEscapeToClose from '../../documents/useEscapeToClose';

// Minimal premium modal shell (no react-bootstrap), matching the Operations
// modal recipe (ReferralPayouts.MarkPaidModal). Backdrop click + Escape close.
export const ModalShell = ({ title, onClose, children }) => {
  useEscapeToClose(onClose);
  return (
    <div
      className="fixed inset-0 z-[1060] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px] animate-in fade-in-0 duration-150"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card shadow-xl animate-in fade-in-0 zoom-in-95 duration-150">
        {/* pr-2.5 (not pr-5) pulls the X's HITBOX into the corner so the icon
            reads as anchored there instead of floating inside the padding. */}
        <div className="flex items-center justify-between border-b border-border py-2.5 pl-5 pr-2.5">
          <h3 className="text-base font-semibold tracking-tight text-foreground">
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 px-5 pb-5 pt-4">{children}</div>
      </div>
    </div>
  );
};

// Generic confirm dialog. `destructive` renders a solid red confirm (delete /
// reject / deactivate); everything else uses the default primary.
export const ConfirmModal = ({
  title,
  body,
  confirmLabel,
  destructive = false,
  onClose,
  onConfirm,
  isSaving,
}) => (
  <ModalShell title={title} onClose={onClose}>
    <div className="text-sm leading-relaxed text-muted-foreground">{body}</div>
    <div className="flex justify-end gap-2">
      <Button variant="outline" onClick={onClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button
        variant={destructive ? 'destructive' : 'default'}
        onClick={onConfirm}
        disabled={isSaving}
      >
        {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {confirmLabel}
      </Button>
    </div>
  </ModalShell>
);

// Follow-up requires a note — it's what the next reviewer (or future you)
// reads to know what's still missing.
export const FollowUpModal = ({ applicant, onClose, onSubmit, isSaving }) => {
  const [note, setNote] = useState('');
  return (
    <ModalShell title="Mark for follow-up" onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        Move{' '}
        <span className="font-medium text-foreground">{applicant.name}</span> to
        follow-up so the team knows their application isn&apos;t ready to
        approve yet.
      </p>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          What needs following up? (required)
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="e.g. W-9 is missing a signature — emailed them to re-sign"
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit({ note: note.trim() })}
          disabled={isSaving || !note.trim()}
        >
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Mark for follow-up
        </Button>
      </div>
    </ModalShell>
  );
};

export const RejectModal = ({ applicant, onClose, onSubmit, isSaving }) => {
  const [note, setNote] = useState('');
  return (
    <ModalShell title="Reject application" onClose={onClose}>
      <p className="text-sm text-foreground">
        Reject <span className="font-medium">{applicant.name}</span>&apos;s
        referral partner application?{' '}
        <span className="font-medium">
          No email will be sent to the applicant.
        </span>
      </p>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Reason (optional)
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="e.g. Duplicate application"
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={() => onSubmit({ note: note.trim() })}
          disabled={isSaving}
        >
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Reject
        </Button>
      </div>
    </ModalShell>
  );
};

// Multi-file PDF picker for hand-collected paperwork (payee applications or
// legacy partners). Files accumulate across picks so users can add one at a time.
export const UploadPaperworkModal = ({
  title = 'Upload paperwork',
  description,
  onClose,
  onUpload,
  isSaving,
}) => {
  const [files, setFiles] = useState([]);
  const inputRef = useRef(null);

  const addFiles = (picked) => {
    // Cap at 10 to mirror the server's limit.
    setFiles((prev) => [...prev, ...Array.from(picked)].slice(0, 10));
  };

  return (
    <ModalShell title={title} onClose={onClose}>
      {description ? (
        <p className="text-sm text-muted-foreground">{description}</p>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = ''; // allow re-picking the same file
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="flex w-full flex-col items-center gap-1 rounded-lg border border-dashed border-border px-4 py-6 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        <Upload className="h-5 w-5" />
        Choose PDF files (up to 10)
      </button>
      {files.length ? (
        <ul className="space-y-1">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center justify-between gap-2 rounded-md bg-muted px-3 py-1.5 text-sm text-foreground"
            >
              <span className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="truncate">{f.name}</span>
              </span>
              <button
                type="button"
                onClick={() =>
                  setFiles((prev) => prev.filter((_, idx) => idx !== i))
                }
                className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
                aria-label={`Remove ${f.name}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={() => onUpload(files)}
          disabled={isSaving || files.length === 0}
        >
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Upload {files.length > 0 ? `(${files.length})` : ''}
        </Button>
      </div>
    </ModalShell>
  );
};
